import { useState } from "react";
import { PageHeader } from "../components/shell/PageHeader";
import { Button } from "../components/ui/Button";
import { EmptyState, ErrorNotice, Notice } from "../components/ui/Feedback";
import { Metric, MicroLabel, SectionTitle } from "../components/ui/Text";
import { ApiError } from "../lib/api";
import { since, timestamp } from "../lib/format";
import {
  consequenceOf,
  flowOptions,
  isAlreadyPinned,
  provenanceOf,
  usePaymentFlow,
  useSetPaymentFlow,
} from "../lib/paymentFlow";
import { useSession } from "../lib/session";
import type { PaymentFlow, PaymentFlowSetting } from "../lib/types";

/**
 * The choices an operator can change without a deploy.
 *
 * One so far, and it is the heaviest one in the product: whether a public charge
 * takes a stranger's money or only holds it (pay-ui spec §11.3). It lives on a page
 * of its own rather than in a corner of the fleet view because it is not a
 * preference — it decides what a card is debited before anyone has charged anything.
 *
 * ## Why the page is mostly prose
 *
 * Both values are defensible and neither is obviously safer, so a bare dropdown
 * labelled "Payment flow: purchase / pre_auth" would be a guess dressed as a
 * control. What the operator needs is what each one does to the driver's statement,
 * where the current value came from, and the promise that flipping it cannot disturb
 * money already taken. All three are on the page, next to the switch.
 */
export function Settings() {
  const { can } = useSession();
  const setting = usePaymentFlow();

  return (
    <>
      <PageHeader
        eyebrow="Configuration"
        title="Settings"
        metrics={
          setting.data === undefined ? undefined : (
            <>
              <Metric
                label="Public charging"
                value={setting.data.flow === "pre_auth" ? "Holds" : "Takes"}
                hint={
                  setting.data.flow === "pre_auth"
                    ? "authorised, not debited"
                    : "debited up front, refunded after"
                }
              />
              <Metric
                label="Last changed"
                value={since(setting.data.updatedAt)}
                tone="muted"
                hint={provenanceOf(setting.data).label.toLowerCase()}
              />
            </>
          )
        }
        lede="Changes here take effect on the next order. Nothing already in flight moves."
      />

      {setting.isError ? <FailedToLoad error={setting.error} /> : null}

      {setting.isPending ? <Skeleton /> : null}

      {setting.data === undefined ? null : (
        <PaymentFlowSwitch setting={setting.data} canWrite={can("settings.update_payment_flow")} />
      )}
    </>
  );
}

/**
 * A 404 here is not a broken panel. `api/index.ts` mounts the settings router only
 * where there is a store to write to and a configured flow to fall back on, so an
 * older server or a deployment without a payment rail answers "No such endpoint" —
 * which is true, and tells an operator nothing about what to do.
 */
function FailedToLoad({ error }: { readonly error: unknown }) {
  if (error instanceof ApiError && error.isMissing) {
    return (
      <EmptyState
        title="This server does not offer any settings."
        detail="The settings API is mounted only where there is a payment rail to configure and somewhere to store the answer. On a fleet-only deployment there is nothing here to change."
      />
    );
  }

  return <ErrorNotice error={error} />;
}

function Skeleton() {
  return (
    <div className="flex flex-col gap-4 pt-section" aria-hidden="true">
      <span className="stage block h-3 w-40 bg-paper-sunken" />
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {[0, 1].map((index) => (
          <span
            key={index}
            className="stage block h-32 bg-paper-sunken"
            style={{ ["--stage-index" as string]: index }}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * The switch itself.
 *
 * Two cards rather than a `<select>`: the options are not values of a field, they
 * are two different things to do to somebody's card, and each needs two sentences to
 * be chosen responsibly. A native radio inside each label keeps arrow-key
 * navigation, the grouping, and the announced state without rebuilding any of it.
 */
function PaymentFlowSwitch({
  setting,
  canWrite,
}: {
  readonly setting: PaymentFlowSetting;
  readonly canWrite: boolean;
}) {
  const save = useSetPaymentFlow();
  /**
   * `undefined` means "whatever the server says". Kept separate from the server's
   * value so a refetch cannot silently move a selection an operator is still looking
   * at, and so a successful save drops back to following the server rather than
   * pinning a stale local choice.
   */
  const [chosen, setChosen] = useState<PaymentFlow | undefined>(undefined);
  const [saved, setSaved] = useState<string | undefined>(undefined);

  const options = flowOptions(setting);
  const selected = chosen ?? setting.flow;
  const provenance = provenanceOf(setting);
  const consequence = consequenceOf(setting.flow, selected);
  const pinned = isAlreadyPinned(selected, setting);

  return (
    <section className="flex flex-col gap-5 pt-section">
      <SectionTitle>How public charging takes money</SectionTitle>

      <fieldset className="m-0 border-0 p-0" disabled={!canWrite || save.isPending}>
        <legend className="sr-only">Payment flow for new public charge orders</legend>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {options.map((option) => {
            const unavailable = option.disabledReason !== undefined;
            const isSelected = selected === option.value;

            return (
              <label
                key={option.value}
                className={[
                  "group flex cursor-pointer flex-col gap-2 border-l-2 px-4 py-4",
                  "transition-colors duration-fast ease-swiss",
                  unavailable
                    ? "cursor-not-allowed border-dashed border-rule bg-transparent"
                    : isSelected
                      ? "border-ink bg-paper-raised"
                      : "border-rule bg-transparent hover:border-ink-faint hover:bg-paper-raised",
                  // The whole card is the focus target, so the ring has to be on it
                  // rather than on the 16px control the operator is not looking at.
                  "has-[:focus-visible]:border-signal has-[:focus-visible]:bg-paper-raised",
                ].join(" ")}
              >
                <span className="flex items-baseline gap-2">
                  <input
                    type="radio"
                    name="payment-flow"
                    value={option.value}
                    checked={isSelected}
                    disabled={unavailable}
                    onChange={() => {
                      setChosen(option.value);
                      setSaved(undefined);
                    }}
                    className="size-4 accent-signal"
                  />
                  <span
                    className={[
                      "text-section font-semibold",
                      unavailable ? "text-ink-faint" : "text-ink",
                    ].join(" ")}
                  >
                    {option.label}
                  </span>
                  {setting.flow === option.value ? (
                    <MicroLabel className="ml-auto">In use</MicroLabel>
                  ) : null}
                </span>

                <span
                  className={[
                    "max-w-prose text-body",
                    unavailable ? "text-ink-faint" : "text-ink-muted",
                  ].join(" ")}
                >
                  {option.summary}
                </span>

                {option.disabledReason === undefined ? null : (
                  <span className="max-w-prose border-t border-rule pt-2 text-micro tracking-normal normal-case text-ink-faint">
                    {option.disabledReason}
                  </span>
                )}
              </label>
            );
          })}
        </div>
      </fieldset>

      {/*
        The consequence is `warn`, not `error`. Nothing is wrong — the operator is
        about to do something deliberate — and rendering an intended action in the
        refusal colour is how a refusal colour stops meaning anything.
      */}
      {consequence === undefined ? null : <Notice tone="warn">{consequence}</Notice>}

      {saved === undefined ? null : <Notice tone="ok">{saved}</Notice>}
      {save.isError ? <ErrorNotice error={save.error} /> : null}

      {canWrite ? (
        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="primary"
            disabled={pinned}
            pending={save.isPending}
            onClick={() => {
              save.mutate(selected, {
                onSuccess: () => {
                  setSaved(
                    selected === setting.flow
                      ? "Stored. This is now pinned in the database rather than following the server's configuration."
                      : `Stored. New orders will ${selected === "pre_auth" ? "hold" : "take"} the driver's money from now on.`,
                  );
                  setChosen(undefined);
                },
              });
            }}
          >
            {actionLabel(setting, selected)}
          </Button>

          {chosen === undefined || chosen === setting.flow ? null : (
            <Button variant="ghost" onClick={() => setChosen(undefined)}>
              Cancel
            </Button>
          )}

          {pinned ? (
            <span className="text-micro tracking-normal normal-case text-ink-faint">
              Already stored. Choose the other option to change it.
            </span>
          ) : null}
        </div>
      ) : (
        <p className="m-0 max-w-prose text-body text-ink-muted">
          Your account can read this setting but not change it. Changing how money is taken needs
          the <span className="font-medium text-ink">Change the payment flow</span> permission.
        </p>
      )}

      <div className="flex flex-col gap-2 border-t border-rule pt-3">
        <MicroLabel>{provenance.label}</MicroLabel>
        <p className="m-0 max-w-prose text-body text-ink-muted">{provenance.detail}</p>
        {setting.updatedAt === null ? null : (
          <p className="m-0 text-micro tracking-normal normal-case text-ink-faint">
            Last written {timestamp(setting.updatedAt, "UTC")} UTC. Who changed it, and what it was
            before, is in Events.
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * Three different acts share one button, and the label has to say which one it is.
 *
 * Storing the value that is already effective is not a no-op: it pins the flow in the
 * database so a later edit of `PUBLIC_PAYMENT_FLOW` cannot move it, and it puts a
 * named operator in the audit trail where there was only a default. "Save" would
 * describe all three and explain none.
 */
function actionLabel(setting: PaymentFlowSetting, selected: PaymentFlow): string {
  if (selected !== setting.flow) {
    return selected === "pre_auth" ? "Switch to holding" : "Switch to taking";
  }
  return setting.source === "stored" ? "No change to save" : "Pin this setting";
}
